<script setup lang="ts">
import { ref, onMounted, computed, getCurrentInstance, nextTick } from 'vue';
import AuthLayout from '../components/AuthLayout.vue';
import { Button, Input } from '../components/ui';
import { GoogleAuthentication, AppleAuthentication } from '../components/sso';
import { useAuthProvidersStore } from '../stores/authProviders';
import { graphqlQuery, SIGN_IN_MUTATION, type SignInResponse } from '../graphql';
import { successToast } from '../services/toasts';
import { completeAuthentication, setAuthCookies } from '../services/authCookies';
import { isValidEmail } from '../services/validators';
import { signInPath } from '../services/authPaths';
import {
  describeSignInFailure,
  GENERIC_SIGN_IN_FAILURE,
  type SignInFailure,
  type SignInRecoveryAction,
} from '../services/signInFailure';

const instance = getCurrentInstance();
const router = instance?.proxy?.$router;
const authProvidersStore = useAuthProvidersStore();

const email = ref('');
const password = ref('');
const emailError = ref('');
const passwordError = ref('');
const failure = ref<SignInFailure | null>(null);
const failureElement = ref<HTMLElement | null>(null);
const loading = ref(false);

const authProviders = computed(() => authProvidersStore.authProviders.value);

const hasAuth0VtsDurstAdapter = computed(() =>
  authProviders.value.some(provider => provider.provider === 'auth0_vts_durst')
);

const auth0VtsDurstAdapterUrl = computed(() => {
  const provider = authProviders.value.find(p => p.provider === 'auth0_vts_durst');
  return provider?.url || '';
});

onMounted(() => {
  email.value = authProvidersStore.email.value || '';

  if (authProviders.value.length === 0) {
    router?.push(signInPath(email.value));
  }
});

function validateForm(): boolean {
  emailError.value = '';
  passwordError.value = '';
  failure.value = null;

  if (!email.value || !isValidEmail(email.value)) {
    emailError.value = 'Enter a valid email.';
    return false;
  }

  if (!password.value) {
    passwordError.value = 'Enter a valid password';
    return false;
  }

  return true;
}

async function showFailure(nextFailure: SignInFailure) {
  failure.value = nextFailure;
  await nextTick();
  failureElement.value?.focus();
}

function goToMagicLink() {
  const query = email.value ? `?email=${encodeURIComponent(email.value)}&auto_submit=true` : '';
  window.location.href = `/users/magic_link/new${query}`;
}

const recoveryActions: Record<SignInRecoveryAction, () => void> = {
  requestMagicLink: goToMagicLink,
};

function recover(action: SignInRecoveryAction) {
  recoveryActions[action]();
}

async function signInWithPassword() {
  if (!validateForm()) return;

  loading.value = true;

  try {
    const response = await graphqlQuery<SignInResponse>(SIGN_IN_MUTATION, {
      email: email.value,
      password: password.value,
      clientId: window.__OAUTH_CLIENT_ID__ || '',
    });

    const accessToken = response.signIn?.accessToken;

    if (accessToken?.token) {
      setAuthCookies(accessToken);
      successToast('Successfully authenticated');
      completeAuthentication();
      return;
    }

    await showFailure(describeSignInFailure(response.signIn?.errors, email.value));
  } catch {
    await showFailure(GENERIC_SIGN_IN_FAILURE);
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <AuthLayout title="Welcome">
    <section
      v-if="failure && failure.kind === 'confirmationRequired'"
      ref="failureElement"
      tabindex="-1"
      aria-labelledby="sign-in-confirmation-title"
      aria-describedby="sign-in-confirmation-message"
      data-test="sign-in-error"
      class="mb-5 rounded border border-sw-purple/60 bg-sw-purple/10 p-4 text-left text-white focus:outline-2 focus:outline-offset-2 focus:outline-white"
    >
      <h2 id="sign-in-confirmation-title" class="m-0 mb-2 text-lg font-semibold leading-snug">{{ failure.heading }}</h2>
      <p id="sign-in-confirmation-message" class="m-0 mb-2 text-sm leading-relaxed break-words">
        {{ failure.message }}
      </p>
      <p class="m-0 mb-3 text-sm leading-relaxed text-white/75">{{ failure.hint }}</p>
      <Button type="button" variant="outline" @click.native="recover(failure.recovery.action)">
        {{ failure.recovery.label }}
      </Button>
    </section>

    <p
      v-else-if="failure"
      ref="failureElement"
      tabindex="-1"
      data-test="sign-in-error"
      class="text-sw-error text-[0.9rem] leading-[1.82] mb-[10px] rounded focus:outline-2 focus:outline-offset-4 focus:outline-white"
    >
      {{ failure.message }}
    </p>

    <form @submit.prevent="signInWithPassword">
      <!-- Email (disabled) -->
      <Input
        id="sign-in-email"
        v-model="email"
        type="email"
        label="Email"
        placeholder="Email"
        autocomplete="email"
        :error="emailError"
        disabled
      />

      <!-- Password -->
      <Input
        id="sign-in-password"
        v-model="password"
        type="password"
        label="Password"
        placeholder="Password"
        autocomplete="current-password"
        :error="passwordError"
      />

      <div class="mt-form-gap">
        <Button type="submit" variant="primary" size="lg" :loading="loading" :disabled="loading"> Sign in </Button>
      </div>
    </form>

    <!-- Divider -->
    <div class="flex items-center my-5">
      <div class="flex-1 border-t border-white/20"></div>
      <span class="px-4 text-white/60 text-sm">or</span>
      <div class="flex-1 border-t border-white/20"></div>
    </div>

    <!-- SSO Buttons -->
    <div class="flex flex-col items-center gap-4">
      <!-- Magic Link -->
      <button
        type="button"
        @click="goToMagicLink"
        class="w-sso-button h-sso-button flex items-center rounded border border-[#d1d1d1] bg-white text-sw-dark text-sm shadow-sm cursor-pointer transition-shadow hover:shadow-md"
      >
        <svg class="ml-[13px] w-4 h-4" viewBox="0 0 512 512" fill="currentColor">
          <path
            d="M48 64C21.5 64 0 85.5 0 112c0 15.1 7.1 29.3 19.2 38.4L236.8 313.6c11.4 8.5 27 8.5 38.4 0L492.8 150.4c12.1-9.1 19.2-23.3 19.2-38.4c0-26.5-21.5-48-48-48H48zM0 176V384c0 35.3 28.7 64 64 64H448c35.3 0 64-28.7 64-64V176L294.4 339.2c-22.8 17.1-54 17.1-76.8 0L0 176z"
          />
        </svg>
        <span class="mx-auto font-medium">Sign in with email link</span>
      </button>

      <!-- Continue with Durst (if available) -->
      <a
        v-if="hasAuth0VtsDurstAdapter"
        :href="auth0VtsDurstAdapterUrl"
        class="w-sso-button h-sso-button flex items-center justify-center rounded border border-[#d1d1d1] bg-white text-sw-dark text-sm shadow-sm cursor-pointer transition-shadow no-underline hover:shadow-md"
      >
        <span class="font-medium">Continue with Durst</span>
      </a>

      <!-- Apple Sign In -->
      <AppleAuthentication />

      <!-- Google Sign In -->
      <GoogleAuthentication />
    </div>
  </AuthLayout>
</template>
